import { ArrowLeft, Loader2 } from 'lucide-react';
import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import { useOAuthStore } from '@/features/auth/store';
import { checkOnboardingVoucher } from '@/lib/api/snappy';
import { cn } from '@/lib/utils';

import { useFlowFlagsStore } from '../flagsStore';
import { useFlowStore } from '../store';
import {
    isV2VoucherCode,
    normalizeVoucherCode,
    redeemV2Code,
    UNLOCK_COPY,
    unlockErrorMessage,
} from '../unlock';

export const FEATURE_FLAGS_COPY = {
    enabledV2:
        'Enabled. Click V1 in the top bar to switch to V2 and set up your browser wallet.',
    enabledV1:
        'Enabled. Click V2 in the top bar to switch to V1 and set up your account.',
    alreadyEnabled: 'Already enabled.',
    ...UNLOCK_COPY,
} as const;

type Message = { tone: 'success' | 'error'; text: string };

interface FeatureFlagsViewProps {
    onBack: () => void;
}

/**
 * The body of the account dialog's "Feature flags" view: one code input and
 * one button, nothing explained (owner instruction). A code ending in `V2`
 * admits the identity to the browser wallet flow; any other code is checked
 * (nothing spent) and remembered for the V1 voucher step. Neither switches
 * anything: the V1/V2 button in the top bar does.
 */
const FeatureFlagsView: React.FC<FeatureFlagsViewProps> = ({ onBack }) => {
    const accountHash = useOAuthStore((state) =>
        state.me?.authenticated ? (state.me.accountHash ?? null) : null,
    );
    const active = useFlowStore((state) => state.active);
    const refreshAvailability = useFlowStore(
        (state) => state.refreshAvailability,
    );
    const setV1Code = useFlowFlagsStore((state) => state.setV1Code);
    const [code, setCode] = useState('');
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<Message | null>(null);

    const submit = async () => {
        const normalized = normalizeVoucherCode(code);
        if (!normalized || busy) return;
        setBusy(true);
        setMessage(null);
        try {
            if (isV2VoucherCode(normalized)) {
                const result = await redeemV2Code(normalized);
                if (!result.ok) {
                    setMessage({ tone: 'error', text: result.message });
                    return;
                }
                setMessage({
                    tone: 'success',
                    text:
                        active === 'v2'
                            ? FEATURE_FLAGS_COPY.alreadyEnabled
                            : FEATURE_FLAGS_COPY.enabledV2,
                });
                return;
            }

            const check = await checkOnboardingVoucher(normalized);
            if (!check.valid) {
                setMessage({ tone: 'error', text: FEATURE_FLAGS_COPY.invalid });
                return;
            }
            if (accountHash) setV1Code(accountHash, normalized);
            void refreshAvailability();
            setMessage({
                tone: 'success',
                // a wallet-first user (no social identity) only has V1
                text:
                    active === 'v2'
                        ? FEATURE_FLAGS_COPY.enabledV1
                        : FEATURE_FLAGS_COPY.alreadyEnabled,
            });
        } catch (error) {
            setMessage({ tone: 'error', text: unlockErrorMessage(error) });
        } finally {
            setBusy(false);
        }
    };

    return (
        <form
            className="flex flex-col gap-3"
            onSubmit={(event) => {
                event.preventDefault();
                void submit();
            }}
        >
            <input
                value={code}
                onChange={(event) => setCode(event.target.value)}
                placeholder="Code"
                aria-label="Code"
                autoComplete="off"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck={false}
                enterKeyHint="go"
                className="h-11 w-full rounded-lg border border-border bg-background px-3 font-mono text-sm text-foreground outline-none focus:border-primary"
            />
            <Button
                type="submit"
                className="w-full font-semibold"
                disabled={busy || code.trim().length === 0}
            >
                {busy && <Loader2 className="size-4 animate-spin" />}
                Enable
            </Button>

            {message && (
                <p
                    role={message.tone === 'error' ? 'alert' : 'status'}
                    className={cn(
                        'text-sm',
                        message.tone === 'error'
                            ? 'text-destructive'
                            : 'text-success',
                    )}
                >
                    {message.text}
                </p>
            )}

            <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={onBack}
                className="justify-self-start self-start text-muted-foreground"
            >
                <ArrowLeft className="size-4" />
                Back
            </Button>
        </form>
    );
};

FeatureFlagsView.displayName = 'FeatureFlagsView';

export default FeatureFlagsView;
